// useCanvasZoom — zoom-mode-resolution + scale + canvas-bounds/origin +
// scroll-centrering för WorkflowCanvas. Extraherad ur WorkflowCanvas.tsx
// (#2 i PRE_RELEASE_REVIEW_100, slice 1) så komponenten närmar sig
// presentational och zoom-mathen blir läsbar i isolation.
//
// Äger:
//   • tier-clampad zoom-mode (static/dynamic + variant-override)
//   • contentScale via useCanvasScale (wheel + knappar)
//   • canvas-bbox + asymmetrisk origin (computeDynamicCanvasSize),
//     med freeze-ref så origin inte hoppar mid-drag
//   • initial scroll-till-world-(0,0)-center (ResizeObserver-gated)
//   • zoom-to-viewport-center-layouteffekten
//   • getOrigin för screen↔world-konvertering
//
// Fit-to-screen bor kvar i WorkflowCanvas — den behöver
// allBlockDimensions från buildAnchorIndex som beräknas senare i
// komponentens flöde.

import { useRef, useEffect, useLayoutEffect, useMemo, useCallback, type RefObject } from 'react';
import type { WorkflowBlock as WorkflowBlockType, BlockGroup } from '@/types/workflow';
import { computeDynamicCanvasSize } from '@/lib/canvasBounds';
import { usePlan } from '@/hooks/usePlan';
import { useIsMobile } from '@/hooks/use-mobile';
import { resolveEffectiveZoom } from '@/lib/effectiveZoomMode';
import { useZoomVariant } from '@/lib/zoomVariant';
import { useCanvasScale } from '@/functions/canvas-scale';

interface UseCanvasZoomArgs {
  canvasRef: RefObject<HTMLDivElement>;
  blocks: WorkflowBlockType[];
  groups: BlockGroup[];
  /** /preview-routen tvingar 'dynamic' så over-limit workflows inte klipps. */
  forceZoomMode?: 'static' | 'dynamic';
}

export function useCanvasZoom({ canvasRef, blocks, groups, forceZoomMode }: UseCanvasZoomArgs) {
  // Tier-clampad zoom. limits.zoomMin/zoomMax + zoomMode kommer från
  // PlanLimits (Fas 2a). Båda modes använder `transform: scale(contentScale)`
  // på canvas-inner-wrappern (uniform shrink där block + spacing +
  // connectors krymper proportionellt tillsammans). SKILLNADEN ligger i
  // canvas-coord-rymden:
  //
  //   static  (Free, Advanced, Pro): coord-rymden är FAST.
  //     Zoom-out shrinkar bara visuellt — user får INTE mer arbetsyta.
  //
  //   dynamic (Max, Enterprise): coord-rymden VÄXER med zoom-out.
  //     Zoom-out dubblar coord-rymden (vid 0.5×) → mer plats för block.
  //
  // Fas 2e: resolveEffectiveZoom() tvångar mobile till 'static' oavsett
  // tier. Mobile får också viewport-fit-floor om content är större än
  // viewport så fit-to-screen alltid funkar.
  const { limits: planLimits, tier } = usePlan();
  const isMobile = useIsMobile();
  const [zoomVariant] = useZoomVariant();
  // Variant-override aktiv ENBART för Max + Enterprise. Andra tiers följer
  // tier-default oavsett localStorage-värde — så en user som downgrade:ar
  // från Max → Free inte sitter fast i en variant de inte längre kan
  // toggla bort. Popovern visas också bara för Max/Enterprise.
  const variantOverrideAllowed = tier === 'max' || tier === 'enterprise';
  const effectiveZoomVariant = variantOverrideAllowed ? zoomVariant : 'tier-default';
  const effectiveZoom = useMemo(() => {
    const eff = resolveEffectiveZoom({ limits: planLimits, isMobile });
    if (forceZoomMode) {
      return { mode: forceZoomMode, min: forceZoomMode === 'dynamic' ? 0.25 : eff.min, max: eff.max };
    }
    // Test-toggle override: user kan tvinga static eller dynamic via canvas-
    // header-popovern. tier-default = ingen override.
    if (effectiveZoomVariant === 'force-static') {
      return { ...eff, mode: 'static' as const };
    }
    if (effectiveZoomVariant === 'dynamic-with-transition' || effectiveZoomVariant === 'dynamic-no-transition') {
      return { mode: 'dynamic' as const, min: 0.25, max: 1.0 };
    }
    return eff;
  }, [planLimits, isMobile, forceZoomMode, effectiveZoomVariant]);
  const isStaticMode = effectiveZoom.mode === 'static';
  // Dynamic-with-transition återställer pre-split-beteendet (block kan
  // hoppa pga multiplikations-mismatch). Dynamic-no-transition är
  // block-jump-fixen (instant snap).
  const useDynamicTransition = effectiveZoomVariant === 'dynamic-with-transition';

  const { contentScale, setContentScale, handleScaleUp, handleScaleDown, handleResetScale } = useCanvasScale(
    canvasRef,
    {
      min: effectiveZoom.min,
      max: effectiveZoom.max,
      enabled: true,
    },
  );

  // Coord-rymd-divider: 1 i static (FAST canvas), contentScale i dynamic
  // (delar med scale så coord-space VÄXER vid zoom-out).
  const coordSpaceDivider = isStaticMode ? 1 : contentScale;

  // Canvas-bbox + origin. Frusen mid-drag via `frozenCanvasBoundsRef` så
  // origin inte hoppar medan user håller en pekare nedtryckt. Annars
  // skulle drag-mathen `reqY = currentY - origin.y` kompensera för
  // origin-shifts genom att flytta block-world-y → scenen "driftar"
  // under cursorn. Bbox recomputeras direkt när drag släpps.
  // forceZoomMode='dynamic' kringgår tier-clampen helt — preview-routen
  // måste kunna visa over-limit workflows utan att klippas.
  const computeBounds = useMemo(
    () => () => computeDynamicCanvasSize(
      blocks,
      groups,
      forceZoomMode === 'dynamic' ? null : planLimits,
    ),
    [blocks, groups, planLimits, forceZoomMode],
  );
  const frozenCanvasBoundsRef = useRef<ReturnType<typeof computeDynamicCanvasSize> | null>(null);
  const liveBounds = useMemo(() => computeBounds(), [computeBounds]);
  const { width: dynamicCanvasWidth, height: dynamicCanvasHeight, originX: canvasOriginX, originY: canvasOriginY } =
    frozenCanvasBoundsRef.current ?? liveBounds;
  // Asymmetric origin → ref:as så touch-handlern (som inte re-runs vid
  // varje frame) kan läsa current-frame-värdet i sin event-listener.
  const canvasOriginXRef = useRef(canvasOriginX);
  const canvasOriginYRef = useRef(canvasOriginY);
  useEffect(() => {
    canvasOriginXRef.current = canvasOriginX;
    canvasOriginYRef.current = canvasOriginY;
  }, [canvasOriginX, canvasOriginY]);

  const hasScrolledRef = useRef(false);

  // Initial scroll: centrera world (0,0) i viewporten. ResizeObserver-
  // gated så ett tomt mount → loadState-flöde (Preview, cloud-load)
  // re-centrerar när blocks dyker upp istället för att fastna i den
  // initiala empty-canvas-positionen.
  useEffect(() => {
    if (hasScrolledRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const doScroll = () => {
      if (hasScrolledRef.current) return;
      if (canvas.clientWidth === 0 || canvas.clientHeight === 0) return;
      const centerX = canvasOriginX - canvas.clientWidth / 2;
      const centerY = canvasOriginY - canvas.clientHeight / 2;
      canvas.scrollTo({ left: Math.max(0, centerX), top: Math.max(0, centerY) });
      if (blocks.length > 0 || groups.length > 0) {
        hasScrolledRef.current = true;
      }
    };
    doScroll();
    if (hasScrolledRef.current) return;
    const observer = new ResizeObserver(() => {
      doScroll();
      if (hasScrolledRef.current) observer.disconnect();
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [canvasRef, canvasOriginX, canvasOriginY, blocks.length, groups.length]);

  // ── Zoom-to-viewport-center ───────────────────────────────────
  // När `contentScale` ändras (knappar, wheel, pinch, reset) ska den
  // världs-koordinat som ligger på viewport-mitten ligga kvar där efter
  // skalningen. Origin-shifts hanteras INTE här (försök i tidigare
  // commit orsakade feedback-loop med scroll-listenern i
  // useCanvasViewport → astronomical canvas-tillväxt).
  const prevScaleRef = useRef(contentScale);
  const prevOriginXRef = useRef(canvasOriginX);
  const prevOriginYRef = useRef(canvasOriginY);
  const prevStaticModeRef = useRef(isStaticMode);
  useLayoutEffect(() => {
    const oldScale = prevScaleRef.current;
    const oldOriginX = prevOriginXRef.current;
    const oldOriginY = prevOriginYRef.current;
    const oldStatic = prevStaticModeRef.current;
    prevScaleRef.current = contentScale;
    prevOriginXRef.current = canvasOriginX;
    prevOriginYRef.current = canvasOriginY;
    prevStaticModeRef.current = isStaticMode;

    if (oldScale === contentScale) return;
    if (!hasScrolledRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const visualCenterX = canvas.scrollLeft + canvas.clientWidth / 2;
    const visualCenterY = canvas.scrollTop + canvas.clientHeight / 2;
    const worldX = oldStatic
      ? visualCenterX / oldScale - oldOriginX
      : (visualCenterX - oldOriginX) / oldScale;
    const worldY = oldStatic
      ? visualCenterY / oldScale - oldOriginY
      : (visualCenterY - oldOriginY) / oldScale;

    const newVisualX = isStaticMode
      ? (canvasOriginX + worldX) * contentScale
      : canvasOriginX + worldX * contentScale;
    const newVisualY = isStaticMode
      ? (canvasOriginY + worldY) * contentScale
      : canvasOriginY + worldY * contentScale;
    canvas.scrollLeft = Math.max(0, newVisualX - canvas.clientWidth / 2);
    canvas.scrollTop = Math.max(0, newVisualY - canvas.clientHeight / 2);
  }, [canvasRef, contentScale, isStaticMode, canvasOriginX, canvasOriginY]);

  const getOrigin = useCallback(() => ({
    // World (0,0) ligger på (canvasOriginX, canvasOriginY) i canvas-
    // koordinatrymden. För Free-tier (fixed canvas) är det mittpunkten;
    // för dynamic mode bestäms det asymmetriskt av computeDynamicCanvasSize
    // så vyn inte hoppar när bara en sida av canvasen växer.
    x: canvasOriginX / coordSpaceDivider,
    y: canvasOriginY / coordSpaceDivider,
  }), [coordSpaceDivider, canvasOriginX, canvasOriginY]);

  return {
    effectiveZoom,
    isStaticMode,
    useDynamicTransition,
    isMobile,
    contentScale,
    setContentScale,
    handleScaleUp,
    handleScaleDown,
    handleResetScale,
    coordSpaceDivider,
    computeBounds,
    frozenCanvasBoundsRef,
    dynamicCanvasWidth,
    dynamicCanvasHeight,
    canvasOriginX,
    canvasOriginY,
    canvasOriginXRef,
    canvasOriginYRef,
    hasScrolledRef,
    getOrigin,
  };
}
